import {
  getAdditionalQueryParams,
  getVisibleFacets,
} from "../../state/modules/search";
import { getDetectedFloor } from "../../state/modules/layout/selectors";
import { getFloorIdByGender } from "asos-site-chrome-store-presentation";
import {
  UNISEX_FLOOR,
  hasFloorFilters,
} from "../../state/modules/page/constants";

export const getFloorFromCookie = () => {
  if (typeof window === "undefined" || !window.asos?.webContext?.getFloor) {
    return null;
  }

  try {
    return window.asos.webContext.getFloor();
  } catch {
    return null;
  }
};

export const getResolvedFloor = (state) =>
  getDetectedFloor(state) || getFloorFromCookie();

export { hasFloorFilters };

export const shouldApplyFloorFilters = (state, additionalQueryParams) => {
  if (hasFloorFilters(additionalQueryParams?.refine)) return false;

  return true;
};

export const buildFloorFacetSelections = (floorFacet, floorId) => {
  const expectedFloorIds = [String(floorId), String(UNISEX_FLOOR)];

  return expectedFloorIds
    .map((id) => {
      const facetValue = floorFacet.facetValues.find(
        (fv) => String(fv.id) === id,
      );

      return facetValue
        ? { facetId: "floor", valueId: facetValue.id, isSelected: true }
        : null;
    })
    .filter(Boolean);
};

const getSelectedFloorFiltersFromFacets = (state) => {
  const facets = getVisibleFacets(state);
  const floorFacet = facets?.find((f) => f.id === "floor");

  if (!floorFacet) return null;

  const selectedFloors = floorFacet.facetValues
    ?.filter((fv) => fv.isSelected)
    ?.map((fv) => fv.id);

  if (!selectedFloors?.length) return null;

  return `floor:${selectedFloors.join(",")}`;
};

// URL helpers
const getUrlRefine = (location) => {
  const urlParams = new URLSearchParams(location?.search || "");

  return urlParams.get("refine");
};

const hasAnyRefineParams = (location) => {
  const refine = getUrlRefine(location);

  return refine && refine.length > 0;
};

const extractFloorFilterFromRefine = (refine) => {
  if (!refine) return null;
  const match = refine.match(/floor:[^|]+/);

  return match ? match[0] : null;
};

const parseFloorIds = (floorFilter) => {
  if (!floorFilter) return [];
  const match = floorFilter.match(/floor:([^|]+)/);
  if (!match) return [];

  return match[1].split(",").sort();
};

const areFloorFiltersEqual = (filter1, filter2) => {
  const ids1 = parseFloorIds(filter1);
  const ids2 = parseFloorIds(filter2);

  return (
    ids1.length === ids2.length && ids1.every((id, index) => id === ids2[index])
  );
};

const getUserModifiedFloorFilters = (state, defaultFilters) => {
  const selectedFloorFilters = getSelectedFloorFiltersFromFacets(state);
  if (
    selectedFloorFilters &&
    !areFloorFiltersEqual(selectedFloorFilters, defaultFilters)
  ) {
    return selectedFloorFilters;
  }

  const additionalQueryParams = getAdditionalQueryParams(state);
  if (hasFloorFilters(additionalQueryParams?.refine)) {
    const floorFilter = extractFloorFilterFromRefine(
      additionalQueryParams.refine,
    );
    if (floorFilter && !areFloorFiltersEqual(floorFilter, defaultFilters)) {
      return floorFilter;
    }
  }

  return null;
};

export const getFloorFiltersForNewSearch = (store, location) => {
  const state = store.getState();
  const dismissedFloors = state.search?.dismissedFloors || {};

  const floor = getResolvedFloor(state);
  const floorId = getFloorIdByGender(floor);

  // Only apply gender filters on men's floor
  if (!floorId || floor !== "men") {
    return null;
  }

  if (hasAnyRefineParams(location)) {
    return null;
  }

  const floorIds = [String(floorId), String(UNISEX_FLOOR)].filter(
    (id) => !dismissedFloors[id],
  );

  if (floorIds.length === 0) {
    return null;
  }

  const defaultFilters = `floor:${floorIds.join(",")}`;
  const userModifiedFilters = getUserModifiedFloorFilters(
    state,
    defaultFilters,
  );

  return { refine: userModifiedFilters || defaultFilters };
};
